/**
 * MathJax only typesets mathematics present at initial page load.
 * Document HTML is injected later via innerHTML, so callers must invoke this
 * after the content is in the DOM. No-ops when MathJax is not on the page.
 *
 * @param {Element} root
 * @returns {Promise<void>}
 */
function typesetMath(root) {
  if (!root) {
    return Promise.resolve()
  }

  return whenMathJaxReady()
    .then((mathJax) => {
      if (!mathJax || typeof mathJax.typesetPromise !== "function") {
        return undefined
      }

      // Clear previous typeset on this subtree when Vue replaces innerHTML.
      if (typeof mathJax.typesetClear === "function") {
        mathJax.typesetClear([root])
      }

      return mathJax.typesetPromise([root])
    })
    .catch((error) => {
      if (process.env.VUE_APP_ENV !== "production"
        && process.env.NODE_ENV !== "staging"
      ) {
        // eslint-disable-next-line no-console
        console.error("[typesetMath] MathJax typeset failed", error)
      }
    })
}

function whenMathJaxReady() {
  if (window.MathJax && typeof window.MathJax.typesetPromise === "function") {
    return window.MathJax.startup && window.MathJax.startup.promise
      ? window.MathJax.startup.promise.then(() => window.MathJax)
      : Promise.resolve(window.MathJax)
  }

  const script = document.getElementById("MathJax-script")

  if (!script) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    const done = () => {
      const mathJax = window.MathJax

      if (mathJax && mathJax.startup && mathJax.startup.promise) {
        mathJax.startup.promise.then(() => resolve(mathJax))
      } else if (mathJax && typeof mathJax.typesetPromise === "function") {
        resolve(mathJax)
      } else {
        resolve(null)
      }
    }

    script.addEventListener("load", done, { once: true })
    script.addEventListener("error", () => resolve(null), { once: true })

    // load may already have fired before the listener was attached
    if (window.MathJax && typeof window.MathJax.typesetPromise === "function") {
      done()
    }
  })
}

async function handleError(inputError) {
  const error = await inputError

  if (error && process.env.VUE_APP_ENV !== "production"
    && process.env.NODE_ENV !== "staging"
  ) {
    // eslint-disable-next-line no-console
    console.error(`Error: ${error}`, error.stack)
  }

  if (error.response) {
    return `error.code.${error.response.status}`
  } else {
    return "error.general"
  }
}

export { handleError, typesetMath }
